import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showAlert } from '../../utils/alert';
import authorizedSignerService, { AuthorizedSignerForm } from '../../services/authorizedSignerService';
import { queryKeys } from '../../queryClient';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';

export function useCreateAuthorizedSigner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AuthorizedSignerForm) => authorizedSignerService.createAuthorizedSigner(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.authorizedSigners.all });
      showAlert('สำเร็จ', 'เพิ่มข้อมูลผู้มีอำนาจลงนามเรียบร้อยแล้ว', 'success');
    },
    onError: (error: any) => {
      console.error('Error creating authorized signer:', error);
      const errorMessage = getApiErrorMessage(error, 'ไม่สามารถเพิ่มข้อมูลผู้มีอำนาจลงนามได้');
      showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
    },
  });
}

export function useUpdateAuthorizedSigner() {
  const queryClient = useQueryClient();

  return useMutation({
    // BUG-S162-P1: forward last-seen updated_at as ifMatch → backend optimistic lock (stale → 409)
    mutationFn: ({ id, data, ifMatch }: { id: number; data: Partial<AuthorizedSignerForm>; ifMatch?: string }) =>
      authorizedSignerService.updateAuthorizedSigner(id, data, ifMatch),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.authorizedSigners.all });
      showAlert('สำเร็จ', 'แก้ไขข้อมูลผู้มีอำนาจลงนามเรียบร้อยแล้ว', 'success');
    },
    onError: (error: any) => {
      console.error('Error updating authorized signer:', error);
      // BUG-S162-P1: optimistic-lock conflict — another tab/user saved first. Tell the user to
      // reload instead of silently overwriting, and refetch the list so the next attempt carries
      // a fresh token (P-S601-1: without this the stale token loops 409 until a full page reload).
      if (error.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: queryKeys.authorizedSigners.all });
        // หน้าแก้ไขเต็มหน้า (28 ต.ค. 2026) โหลดค่าล่าสุดลงฟอร์มให้เองหลัง 409 — ไม่ต้องบอกให้ปิดหน้าต่าง
        showAlert('ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น', 'มีผู้ใช้อื่นบันทึกข้อมูลนี้ไปแล้ว ระบบโหลดข้อมูลล่าสุดลงฟอร์มให้แล้ว กรุณาตรวจสอบแล้วแก้ไขอีกครั้ง', 'error');
        return;
      }
      const errorMessage = getApiErrorMessage(error, 'ไม่สามารถแก้ไขข้อมูลผู้มีอำนาจลงนามได้');
      showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
    },
  });
}

export function useDeleteAuthorizedSigner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => authorizedSignerService.deleteAuthorizedSigner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.authorizedSigners.all });
      showAlert('สำเร็จ', 'ลบข้อมูลผู้มีอำนาจลงนามเรียบร้อยแล้ว', 'success');
    },
    onError: (error: any) => {
      console.error('Error deleting authorized signer:', error);
      const errorMessage = getApiErrorMessage(error, 'ไม่สามารถลบข้อมูลผู้มีอำนาจลงนามได้');
      showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
    },
  });
}